import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@dashboard/components/ui/button';
import PageHeader from '@dashboard/components/PageHeader';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useBlockEmail, useUnblockEmail } from '@dashboard/hooks/useBlockedEmails';
import { confirm } from '@dashboard/lib/confirm';
import { blockedCustomersKey } from '@dashboard/lib/queryKeys';

import BlockedCustomerTable from './components/BlockedCustomerTable';
import BlockedCustomerCreateDialog from './components/BlockedCustomerCreateDialog';
import BlockedCustomerEditDialog from './components/BlockedCustomerEditDialog';

const SettingsCheckoutProtection = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingBlockedCustomer, setEditingBlockedCustomer] = useState(null);
  const [filter, setFilter] = useState('blocked');
  const { sellerId, token } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: [...blockedCustomersKey(sellerId), 'all'],
    queryFn: () => fetcher(`/sellers/${sellerId}/blocked-customers?status=all`, token),
    enabled: !!sellerId,
  });

  const unblockEmail = useUnblockEmail();
  const blockEmail = useBlockEmail();

  const blockedCustomers = useMemo(() => {
    const entries = data?.blockedCustomers ?? [];

    return entries.filter(entry => (filter === 'unblocked' ? entry.isDeleted : !entry.isDeleted));
  }, [data, filter]);

  async function handleUnblock(blockedCustomer) {
    const confirmed = await confirm(`Unblock ${blockedCustomer.email}?`, { confirm: 'Unblock' });

    if (confirmed) {
      unblockEmail.mutate(blockedCustomer.id);
    }
  }

  async function handleBlock(blockedCustomer) {
    const confirmed = await confirm(`Block ${blockedCustomer.email}?`, {
      confirm: 'Block',
      description:
        'This email will not be able to complete any checkout, event, or form page in your store. Existing subscriptions for this email will continue to renew.',
    });

    if (confirmed) {
      blockEmail.mutate({ email: blockedCustomer.email });
    }
  }

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Checkout protection"
        description="Protect your store from unwanted purchases by blocking emails"
        actions={<Button onClick={() => setShowCreateDialog(true)}>Block email</Button>}
      />

      <div className="mb-32">
        <BlockedCustomerTable
          data={{ blockedCustomers }}
          isLoading={isLoading}
          isError={isError}
          filter={filter}
          onFilterChange={setFilter}
          onUnblock={handleUnblock}
          onBlock={handleBlock}
          onEditReason={setEditingBlockedCustomer}
        />

        <BlockedCustomerCreateDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
        />

        {editingBlockedCustomer && (
          <BlockedCustomerEditDialog
            key={editingBlockedCustomer.id}
            open
            blockedCustomer={editingBlockedCustomer}
            onClose={() => setEditingBlockedCustomer(null)}
          />
        )}
      </div>
    </div>
  );
};

export default SettingsCheckoutProtection;
